<template>
	<div ref="container"></div>
</template>

<script>
import ChatWidget from '@/widgets/ChatWidget';
import SoundManager from '@/SoundManager';
import MockSoundManager from '@/MockSoundManager';
import VampiVoiceClient from '@/VampiVoiceClient';
import MmlApiClient from '@/MmlApiClient';
import previewConfig from '@/previewConfig';

// Lazy singletons — created only when audio preview is first enabled,
// then reused across widget reinits so we don't create multiple AudioContexts.
let liveSoundManager = null;
let liveVoiceApi = null;
let liveMmlApi = null;

function getLiveSoundManager() {
	if(!liveSoundManager) liveSoundManager = new SoundManager();
	return liveSoundManager;
}
function getLiveVoiceApi() {
	if(!liveVoiceApi) liveVoiceApi = new VampiVoiceClient(process.env.VUE_APP_VAMPIVOICEAPIURL, process.env.VUE_APP_VAMPIVOICEAPIKEY);
	return liveVoiceApi;
}
function getLiveMmlApi() {
	if(!liveMmlApi) liveMmlApi = new MmlApiClient(process.env.VUE_APP_VAMPIMMLAPIURL, process.env.VUE_APP_VAMPIMMLAPIKEY);
	return liveMmlApi;
}

let msgIdCounter = 0;

const AVATAR_COLORS = [
	'#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
	'#3498db', '#9b59b6', '#e91e63', '#00bcd4', '#ff5722',
];

const mockSoundManager = new MockSoundManager();

function hashCode(str) {
	let h = 0;
	for(let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	return Math.abs(h);
}

function generateInitialsAvatar(username) {
	const initials = username.slice(0, 2).toUpperCase();
	const color = AVATAR_COLORS[hashCode(username) % AVATAR_COLORS.length];
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="${color}"/><text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="13" font-weight="bold" fill="white">${initials}</text></svg>`;
	return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function pickRandom(arr) {
	return arr[Math.floor(Math.random() * arr.length)];
}

export default {
	name: 'ChatWidgetPreview',
	props: {
		settings: { type: Object, default: () => ({}) },
		speed: { type: Number, default: 0.5 },
		initialMessages: Number,
		previewAudio: { type: Boolean, default: false },
		realData: { type: Boolean, default: false },
	},
	data() {
		return {
			services: [],
			voices: [],
			previewIntervalId: null,
		};
	},
	watch: {
		settings: {
			deep: true,
			handler(s) { this._applySettings(s); },
		},
		speed() {
			if(!this.realData) { this._stopPreview(); this._startPreview(); }
		},
		previewAudio() {
			this._destroyWidget();
			this._initWidget();
		},
		realData() {
			this._stopPreview();
			this._destroyWidget();
			this._initWidget();
		},
	},
	mounted() {
		this._initWidget();

		this.$api.get('channels')
			.then(channels => {
				const seen = new Set();
				this.services = channels.filter(c => {
					if(!c.service_icon_url || seen.has(c.service_id)) return false;
					seen.add(c.service_id);
					return true;
				});
			})
			.catch(() => {});

		this.$api.get('userVoices', { default: 1, limit: 100 })
			.then(result => { this.voices = result.results || []; })
			.catch(() => {});

		if(!this.realData) this._startPreview();
	},
	beforeDestroy() {
		this._stopPreview();
		this._destroyWidget();
	},
	methods: {
		async _initWidget() {
			let hash = 'preview';
			try {
				const user = await this.$api.get('userData');
				if(user?.widget_hash) hash = user.widget_hash;
			} catch(e) { /* ignore */ }

			if(!this.$refs.container) return;

			const [eventTypes, giftTypesResponse, giftThresholds, textReplacements, serviceIcons, userData] = await Promise.all([
				this.$api.get(`widget/${encodeURIComponent(hash)}/eventTypes`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/giftTypes`).catch(() => ({ results: [] })),
				this.$api.get(`widget/${encodeURIComponent(hash)}/giftThresholds`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/textReplacements`).catch(() => ({})),
				this.$api.get(`widget/${encodeURIComponent(hash)}/services`).catch(() => []),
				this.$api.get(`widget/${encodeURIComponent(hash)}/userData`).catch(() => null),
			]);

			if(!this.$refs.container) return;

			this.widget = new ChatWidget({
				container: this.$refs.container,
				soundManager: this.previewAudio ? getLiveSoundManager() : mockSoundManager,
				voiceApi: this.previewAudio ? getLiveVoiceApi() : null,
				mmlApi: this.previewAudio ? getLiveMmlApi() : null,
				baseUrl: this.$baseUrl,
			});

			const s = { ...this.settings };
			await this.widget.init({
				settings: s,
				eventTypes,
				giftTypes: giftTypesResponse?.results || [],
				giftThresholds,
				textReplacements,
				serviceIcons,
				userData,
			});

			if(this.realData) {
				this._previewSubId = this.$socket.subscribe(hash, {
					reconnect: () => { this._stopPreview(); this._destroyWidget(); this._initWidget(); },
					chat: data => this.widget?._onChat(data),
				});
			}

			for(let i = 0; i < this.initialMessages; i++)
				this._addFakeMessage();
		},
		_destroyWidget() {
			if(this._previewSubId != null) { this.$socket.unsubscribe(this._previewSubId); this._previewSubId = null; }
			if(this.widget) {
				if(this.widget._currentItem) {
					this.widget._currentItem.sounds?.forEach(s => s?.stop?.());
					this.widget._currentItem.defaultSound?.stop?.();
					this.widget._currentItem = null;
				}
				const cancelled = this.widget.queue.cancel(() => true);
				for(const item of cancelled) {
					item.sounds?.forEach(s => s?.stop?.());
					item.defaultSound?.stop?.();
				}
				this.widget.queue.skip();
				this.widget.destroy();
				this.widget = null;
				if(this.$refs.container) this.$refs.container.innerHTML = '';
			}
		},
		_applySettings(s) {
			if(!this.widget) return;
			this.widget.applySettings({ ...s });
		},
		_startPreview() {
			this.previewIntervalId = setInterval(() => {
				this._addFakeMessage();

			}, 300 + (1 - this.speed) * 9700);
		},
		_stopPreview() {
			if(!this.previewIntervalId) return;
			clearTimeout(this.previewIntervalId);
			this.previewIntervalId = null;
		},
		_addFakeMessage() {
			if(!this.widget) return;

			const username = pickRandom(previewConfig.usernames);
			const isGift = Math.random() < 0.03;
			const messageText = isGift ? null : pickRandom(previewConfig.chatMessages);

			const avatarUrl = previewConfig.avatarUrls?.length
				? pickRandom(previewConfig.avatarUrls)
				: generateInitialsAvatar(username);

			let serviceId = null;
			let serviceIconUrl = null;
			let serviceName = null;
			if(this.services.length > 0) {
				const svc = pickRandom(this.services);
				serviceId = svc.service_id;
				serviceIconUrl = svc.service_icon_url;
				serviceName = svc.service_name || svc.service_id;
			}

			const voice = this.voices.length > 0 ? pickRandom(this.voices) : null;

			// Always route through _onChat so the queue, speaking class, and event
			// sounds all work. The mock sound manager handles the no-audio case.
			this.widget._onChat({
				serviceId: serviceId || 'preview',
				serviceName,
				serviceIconUrl,
				id: `preview-${++msgIdCounter}`,
				sender: {
					displayName: username,
					avatar: avatarUrl,
					isSelf: false,
					settings: {
						voice_provider: voice?.provider,
						voice_id: voice?.id,
					}
				},
				spans: messageText ? [{ text: messageText }] : [],
				message: messageText ? { string: messageText } : null,
				type: isGift ? 'gift' : 'chat',
				giftType: isGift ? 'preview' : undefined,
				value: isGift ? Math.round(1 + Math.random() * 200, 2) : 0,
				currency: isGift ? 'USD' : undefined,
				convertedValue: isGift ? Math.round(1 + Math.random() * 200, 2) : 0,
				convertedCurrency: isGift ? 'USD' : undefined,
			});
		},
	},
};
</script>
